<template lang="pug">
.text-field-tegaki(:data-vertical='isVertical', :style='setStyle')
  //- 入力フィールド部
  .text-field-tegaki__relative
    input.text-field-tegaki__relative__input(
      ref='textfield',
      inputmode='none',
      :style='textPosition',
      @click='show',
      readonly
    )
    .text-field-tegaki__relative__katex(ref='katexfield', @click='show', :style='katexPosition')
      .text-field-tegaki__relative__katex__text(v-html='displayResult')
  //- ダイアログ部
  .text-field-tegaki__dialog(:data-show='isShow', @click='hide')
    .text-field-tegaki__dialog__inner
      .text-field-tegaki__dialog__inner__content(ref='dialogContent', @click.stop)
        //- ヘッダー部
        .text-field-tegaki__dialog__inner__content__header
          .text-field-tegaki__dialog__inner__content__header__result(
            @mousedown='dragDialogStart',
            @mouseup='dragDialogEnd',
            @touchstart='touchDialogStart',
            @touchend='touchDialogEnd'
          )
            .text-field-tegaki__dialog__inner__content__header__result__title
              slot(name='resultTitle')
            input.text-field-tegaki__dialog__inner__content__header__result__input(
              v-model='resultLatex',
              v-if='canStandardKeyboard'
            )
            .text-field-tegaki__dialog__inner__content__header__result__text(v-html='resultHtml', v-else)
          .text-field-tegaki__dialog__inner__content__header__actions
            .text-field-tegaki__dialog__inner__content__header__actions__button(@click='clear')
              image-base(:src='require("@/assets/img/tegaki/drill-btn-clear-inactive.png")')
            .text-field-tegaki__dialog__inner__content__header__actions__button(@click='changeMode("pen")')
              image-base(:src='require(`@/assets/img/tegaki/${iconName("pen")}`)')
            .text-field-tegaki__dialog__inner__content__header__actions__button(@click='changeMode("eraser")')
              image-base(:src='require(`@/assets/img/tegaki/${iconName("eraser")}`)')
            .text-field-tegaki__dialog__inner__content__header__actions__button(@click='complete')
              image-base(:src='require("@/assets/img/tegaki/drill-btn-success-inactive.png")')
        //- キャンバス表示部
        .text-field-tegaki__dialog__inner__content__body(:data-english='isEnglish')
          tegaki-canvas.text-field-tegaki__dialog__inner__content__body__canvas(
            :mode='mode',
            :strokes='strokes',
            @input='stopPen',
            ref='canvas'
          )
        //- フッター部
        .text-field-tegaki__dialog__inner__content__footer
          button-next.text-field-tegaki__dialog__inner__content__footer__button(@click='recognize')
            slot(name='recognizeButtonLabel')
</template>

<script lang="ts">
import { Component, Prop, Vue, Ref } from 'vue-property-decorator'
import ImageBase from '@/components/atoms/ImageBase.vue'
import TegakiCanvas from '@/components/molecules/student/TegakiCanvas.vue'
import ButtonNext from '@/components/molecules/student/ButtonNext.vue'
import { DrawSettings } from '@/models/drawSettings'
import { StrokePoint } from '@/models/StrokePoint'
import { KatexPosition, TextPosition } from '@/models/textFieldKatexStyle'
import { LangType } from '@/models/studyModel'
import MyScript, { MyScriptStroke } from '@/utils/TegakiUtils/MyScript'
import katex from 'katex'

const INPUT_MIN_HEIGHT = 41
const INPUT_MIN_HEIGHT_VERTICAL = 150

/**
 * 手書きダイアログ付き入力フィールド
 * フィールドクリックでダイアログが表示される
 */
@Component({
  components: {
    ImageBase,
    TegakiCanvas,
    ButtonNext,
  },
})
export default class TextFieldTegaki extends Vue {
  // 入力値
  @Prop()
  value!: string | null

  // MyScriptに渡す言語タイプ
  @Prop()
  lang!: LangType

  // 保存済みペンストローク
  @Prop()
  savedStrokes?: StrokePoint[][]

  // 通常キーボード使用可能フラグ
  @Prop({ default: false })
  canStandardKeyboard?: boolean

  // 英文フラグ
  @Prop({ default: false })
  isEnglish?: boolean

  // 縦書きフラグ
  @Prop({ default: false })
  isVertical?: boolean

  // 入力フィールドRef
  @Ref() textfield!: HTMLInputElement
  @Ref() katexfield!: HTMLElement

  // ダイアログRef
  @Ref() dialogContent!: HTMLElement
  @Ref() canvas!: TegakiCanvas

  // テキストフィールドとkatexフィールドのサイズ
  private katexPosition: KatexPosition = {
    left: 0,
    top: 0,
    width: 0,
    color: 'rgba(0, 0, 0, 0.6)',
  }
  private textPosition: TextPosition = {
    height: 0,
    width: 0,
  }
  private textObserver: any
  private katexObserver: any

  // キャンバスサイズ
  private width = 0
  private height = 0

  // ペンのモード
  private mode: DrawSettings.TegakiMode = 'pen'

  // ペンストローク
  private strokes: StrokePoint[][] = []

  // 入力結果
  private resultHtml = ''
  private resultLatex = ''

  // ダイアログ表示フラグ
  private isShow = false

  // ダイアログ移動時のクリック/タッチ位置
  private shiftX = 0
  private shiftY = 0

  // MyScript
  private myScript = new MyScript({ lang: this.lang || undefined })

  // キャンバスサイズをスタイルに設定
  get setStyle(): { [key: string]: string } {
    return {
      '--tegaki-canvas-width': `${this.width}px`,
      '--tegaki-canvas-height': `${this.height}px`,
    }
  }

  // 入力フィールドに表示する入力結果
  get displayResult(): string {
    return this.lang === 'MATHML' ? this.value && katex.renderToString(this.value) : this.value
  }

  private beforeMount() {
    // マウント前に値を入力結果に設定
    if (this.value) {
      this.resultLatex = this.value
      this.resultHtml = katex.renderToString(this.value)
    }
    if (this.savedStrokes) this.strokes = this.savedStrokes
  }

  private mounted() {
    window.addEventListener('resize', this.onResize)
    this.onResize()

    // テキストフィールドとkatexフィールドにリサイズオブザーバーを設定する
    this.textObserver = new (window as any).ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.katexPosition = {
          width: `${this.textfield.offsetWidth - 4}px`,
          left: `${this.textfield.offsetLeft + 2}px`,
          top: `${this.textfield.offsetTop + 2}px`,
          color: 'rgb(0, 0, 0)',
        }
      })
    })
    this.textObserver.observe(this.textfield)

    this.katexObserver = new (window as any).ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (this.isVertical) {
          // 縦書き時
          this.textPosition = {
            height: `${this.culcMinNumber(this.katexfield.offsetHeight, INPUT_MIN_HEIGHT_VERTICAL) + 4}px`,
            width: `${this.culcMinNumber(this.katexfield.offsetWidth, INPUT_MIN_HEIGHT) + 4}px`,
          }
        } else {
          // 横書き時
          this.textPosition = {
            height: `${this.culcMinNumber(this.katexfield.offsetHeight, INPUT_MIN_HEIGHT) + 4}px`,
          }
        }
      })
    })
    this.katexObserver.observe(this.katexfield)
  }

  private culcMinNumber(num: number, min: number) {
    return num > min ? num : min
  }

  private beforeDestroy() {
    this.textObserver.disconnect()
    this.katexObserver.disconnect()
  }

  private onResize() {
    this.width = Math.round(window.innerWidth * 0.9)
    this.height = Math.round(window.innerHeight * 0.3)
  }

  private async recognize() {
    await this.wait(500)

    // ストロークポイントが空の場合は結果に空文字を設定
    if (this.strokes.length === 0) {
      this.resultHtml = ''
      this.resultLatex = ''
      return
    }

    // ストロークポイント配列をMyScript用のストローク配列に変換
    const strokes: MyScriptStroke[] = this.strokes.map((stroke: StrokePoint[]) => ({
      x: stroke.map((t: StrokePoint) => t.x),
      y: stroke.map((t: StrokePoint) => t.y),
    }))

    // MyScript実行
    this.myScript.setStrokes = await strokes
    this.myScript.setSize = await { width: this.width, height: this.height }
    await this.myScript.recognize()

    // 実行結果を結果変数に設定
    this.resultLatex = this.myScript.latex
    this.resultHtml = this.myScript.html
  }

  private async wait(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  // 全消しボタン押下時
  private async clear() {
    this.strokes = []
    await this.recognize()
  }

  // 完了ボタン押下時
  private complete() {
    this.hide()
    // 入力結果とストロークポイントを返す
    this.$emit('input', this.resultLatex)
    this.$emit('set-stroke-data', this.strokes)
  }

  // ペン/消しゴムボタン押下時
  private changeMode(mode: DrawSettings.TegakiMode) {
    this.mode = mode
  }

  // アイコン名取得
  private iconName(mode: DrawSettings.TegakiMode) {
    const activate = this.mode === mode ? 'active' : 'inactive'
    return `drill-btn-${mode}-${activate}.png`
  }

  // ペンストローク完了時
  private async stopPen(strokes: StrokePoint[][]) {
    this.strokes = strokes
  }

  // ダイアログ表示
  private show() {
    if (this.isShow) return

    this.isShow = true
    // キャンバスサイズを計算し、ストロークを描画
    this.$nextTick(() => {
      this.canvas.adjustAndDraw()
    })
  }

  // ダイアログ非表示
  private async hide() {
    await this.wait(100)

    this.isShow = false
    // ダイアログ位置を初期化する
    this.dialogContent.style.left = ''
    this.dialogContent.style.top = ''
  }

  /**
   * マウスドラッグで手書きダイアログを移動する
   */
  // マウスクリック時
  private dragDialogStart(e: MouseEvent) {
    // ダイアログ端からマウス位置への距離を取得
    const rect = this.dialogContent.getBoundingClientRect()
    this.shiftX = e.clientX - rect.left
    this.shiftY = e.clientY - rect.top
    // マウス移動イベントを登録
    window.addEventListener('mousemove', this.moveDialog)
  }
  // マウスを離した時
  private dragDialogEnd() {
    // マウス移動イベントを解除
    window.removeEventListener('mousemove', this.moveDialog)
  }
  // マウス移動時のダイアログ移動イベント
  private moveDialog(e: MouseEvent) {
    // ダイアログのleft/topにマウス位置から距離分を引いた値を設定する
    this.dialogContent.style.left = e.clientX - this.shiftX + 'px'
    this.dialogContent.style.top = e.clientY - this.shiftY + 'px'
  }

  /**
   * タッチイベントで手書きダイアログを移動する
   */
  // 画面タッチ時
  private touchDialogStart(e: TouchEvent) {
    // 他のタッチイベントをキャンセル
    e.preventDefault()
    // ダイアログ端からタッチ位置への距離を取得
    const rect = this.dialogContent.getBoundingClientRect()
    this.shiftX = e.touches[0].clientX - rect.left
    this.shiftY = e.touches[0].clientY - rect.top
    // タッチ移動イベントを登録
    window.addEventListener('touchmove', this.touchMoveDialog)
  }
  // 画面タッチを離した時
  private touchDialogEnd() {
    // タッチ移動イベントを解除
    window.removeEventListener('touchmove', this.touchMoveDialog)
  }
  // タッチ移動時のダイアログ移動イベント
  private touchMoveDialog(e: TouchEvent) {
    // ダイアログのleft/topにタッチ位置から距離分を引いた値を設定する
    this.dialogContent.style.left = e.touches[0].clientX - this.shiftX + 'px'
    this.dialogContent.style.top = e.touches[0].clientY - this.shiftY + 'px'
  }
}
</script>

<style lang="stylus" scoped>
.text-field-tegaki
  &__relative
    position relative

    &__input
      width 10.5rem
      padding 5px
      border-radius 4px
      appearance none
      -webkit-appearance none
      -moz-appearance none

    &__katex
      position absolute
      display flex
      border-radius 4px
      background-color var(--color-input-field-keyboard__background)
      font-size 16px
      overflow auto
      z-index 5
      white-space nowrap
      &::-webkit-scrollbar
        width 8px
        height 8px
      &::-webkit-scrollbar-track
        background var(--color-input-field-keyboard__background)
      &::-webkit-scrollbar-thumb
        background var(--color-input-field-keyboard-scrollbar__background)
        border-radius 8px
        box-shadow inset 0 0 0 2px var(--color-input-field-keyboard__background)

      &__text
        display inline
        flex 1 1 auto
        padding 8px

  &__dialog
    display none
    position fixed
    left 0
    top 0
    width 100%
    height 100%
    z-index 100

    &__inner
      display flex
      width 100%
      height 100%
      justify-content center
      align-items flex-end
      padding-bottom 10px

      &__content
        position absolute
        box-sizing border-box
        border 3px solid var(--color-input-field-tegaki-dialog__border)
        background var(--color-input-field-tegaki-dialog__border)
        z-index 200

        &__header
          display flex
          align-items center
          justify-content space-between
          padding 16px
          height 96px
          &__result
            display flex
            align-items center
            width 80%
            height 100%
          &__actions
            display flex
            &__button
              cursor pointer
              &:not(:first-child)
                margin-left 8px
        &__body
          background var(--color-default__box-background)
          &__canvas
            width var(--tegaki-canvas-width)
            height var(--tegaki-canvas-height)

          &[data-english='true']
            background-image url("~@/assets/img/background-english.png")
            background-repeat repeat-x
            background-size auto 100%

        &__footer
          display flex
          align-items center
          justify-content flex-end
          padding 16px
          height 56px
          &__button
            width 180px

    &[data-show='true']
      display block

  &[data-vertical='true'] &__relative
    &__input
      writing-mode vertical-rl

    &__katex
      writing-mode vertical-rl
      display block
      height 10.5rem
      overflow-y scroll
</style>
